import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PoEmailAttachmentsService } from "../../../../core/services/po-email-attachments.service";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { FileUploadDto } from "../../../../core/models/shared/fileUploadDto";
import { LoaderService } from "../../../../core/services/loader.service";
import { getBase64 } from "../../../../core/services/shared.service";
import { CommonModule } from "@angular/common";
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadModule,
} from "primeng/fileupload";
import { ButtonModule } from "primeng/button";

@UntilDestroy()
@Component({
  selector: "jni-po-email-attachments",
  imports: [CommonModule, FileUploadModule, ButtonModule],
  templateUrl: "./po-email-attachments.component.html",
  styleUrls: ["./po-email-attachments.component.scss"],
})
export class PoEmailAttachmentsComponent {
  fileSizeInKb = 1024;
  filesObject: FileUploadDto[] = [];
  count = 1;
  totalFileSizeInKb = 0;
  fileAttachmentArray = new Map<string, string>();

  @ViewChild("fileEvent", { static: false })
  fileEvent!: FileUpload;
  @Output() attachmentFileArrayEvent = new EventEmitter();
  @Output() attachmentFileArrayUrlEvent = new EventEmitter<
    Map<string, string>
  >();

  @Output() TotalFileSizeEvent = new EventEmitter();

  constructor(
    private _toastr: JniToastrService,
    private _poEmailAttachmentsService: PoEmailAttachmentsService,
    private _loaderService: LoaderService
  ) {}

  async getAttachmentFile(file: FileUploadHandlerEvent) {
    if (!file.files) return;
    const input = file.files;
    for (const key in input) {
      const fileObj = input[JSON.parse(key)];
      const filesize = Math.round(fileObj.size / this.fileSizeInKb);
      const fileSizeName = this.formatBytes(fileObj.size);
      const fileName = fileObj?.name;
      const fileextension =
        "." + (fileObj?.name?.split(".")?.pop() ?? "").toLowerCase();
      if (filesize) {
        this.totalFileSizeInKb = this.totalFileSizeInKb + filesize;
        this.TotalFileSizeEvent.emit(this.totalFileSizeInKb);
        if (this.totalFileSizeInKb >= 33000) {
          this._toastr.error(
            `Total file attachments size must be less than 33MB`,
            "Error"
          );
          return;
        }
      }
      if (this.filesObject.length >= 250) {
        this._toastr.error(`File attachments must be less than 250`, "Error");
        return;
      }
      await getBase64(fileObj).then(data => {
        this.createFileUploadDto(
          data as string,
          fileName,
          fileextension,
          filesize,
          fileObj.type,
          fileSizeName
        );
      });
    }

    // Clear the PrimeNG FileUpload component's internal state to prevent showing file details
    if (this.fileEvent) {
      setTimeout(() => {
        this.fileEvent.clear();
      }, 100);
    }
  }

  getFileUrl(object: FileUploadDto) {
    this._loaderService.setLoaderState(true);
    this._poEmailAttachmentsService
      .getPoEmailAttachmentUrl(object)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res?.data) {
            this.filesObject.push({
              base64: object.base64,
              fileName: object.fileName,
              fileExtension: object.fileExtension,
              fileSize: object.fileSize,
              fileMimeType: object.fileMimeType,
              fileId: this.count++,
              fileSizeName: object.fileSizeName,
            });
            this.fileAttachmentArray.set(object.fileName, res.data);
            this.attachmentFileArrayUrlEvent.emit(this.fileAttachmentArray);
            this.attachmentFileArrayEvent.emit(this.filesObject);
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  onFileClickRemove() {
    this.fileEvent.clear();
  }

  formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  createFileUploadDto(
    data: string,
    name: string,
    extension: string,
    size: number,
    filemimetype: string,
    fileSizeName: string
  ) {
    const base64File = data.split(",")[1];
    const object: FileUploadDto = {
      base64: base64File,
      fileName: name,
      fileExtension: extension,
      fileSize: size,
      fileMimeType: filemimetype,
      fileId: this.count++,
      fileSizeName: fileSizeName,
    };

    this.getFileUrl(object);
  }

  fileRemove(file: FileUploadDto) {
    if (file) {
      const fileObject = this.filesObject.indexOf(file);
      this.filesObject.splice(fileObject, 1);
      this.totalFileSizeInKb = this.totalFileSizeInKb - file.fileSize;
      this.fileAttachmentArray.delete(file.fileName);
      this.attachmentFileArrayUrlEvent.emit(this.fileAttachmentArray);
      this.TotalFileSizeEvent.emit(this.totalFileSizeInKb);
      this.attachmentFileArrayEvent.emit(this.filesObject);
    }
  }

  onViewAttachment(file: FileUploadDto) {
    if (file) {
      const blob = this.b64toBlob(file.base64, file.fileMimeType);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
    }
  }

  b64toBlob(b64Data: string, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  isDragOver = false;

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      // Convert FileList to File array and trigger the existing upload handler
      const fileArray: File[] = Array.from(files);
      const fakeEvent: FileUploadHandlerEvent = {
        files: fileArray,
      };
      this.getAttachmentFile(fakeEvent);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  removeFile(): void {
    // No longer needed since we removed the p-fileupload internal preview
    // File removal is handled by the filesObject array and your custom UI
  }
}
